/**
 * StreamingProcessor - Stream processing orchestration
 *
 * Handles the streaming response loop with dual-path post-processing:
 * - SSE mode: Parses structured SSE events (thinking, text, tool_start, tool_end, done, error)
 * - Legacy mode: Falls back to raw content accumulation with tool detection regex
 *
 * A single stream-reading loop feeds both paths. If any SSE events are detected,
 * SSE mode is used. Otherwise, legacy processing runs as a fallback.
 *
 * @example
 * const processor = new StreamingProcessor({
 *   streamingService: { streamToAsyncIterable, parseChunk },
 *   toolDetectionService: { detectToolExecution },
 *   responseHandler
 * });
 *
 * const result = await processor.processStream(response.response, sessionId, {
 *   // SSE callbacks
 *   onThinking: () => showThinkingState(),
 *   onText: (chunk, accumulated) => updateMessage(accumulated),
 *   onToolStart: (tool, toolId) => showToolBadge(tool),
 *   onToolEnd: (tool, toolId) => dismissToolBadge(tool),
 *   onError: (message) => showError(message),
 *   // Shared callbacks
 *   onChunk: (chunk, count) => console.log(`Chunk ${count}`),
 *   onComplete: (responseData) => finalizeMessage(responseData),
 *   // Legacy callbacks (used when no SSE events detected)
 *   onToolDetected: ({ toolName, params }) => handleTool(toolName, params),
 *   onToolResults: (results) => handleResults(results)
 * });
 */

import SSEFrameParser from './SSEFrameParser';

// Fix #10: Default max content size limit to prevent memory exhaustion attacks
const DEFAULT_MAX_CONTENT_SIZE = 10 * 1024 * 1024; // 10MB

class StreamingProcessor {
  /**
   * @param {Object} options
   * @param {Object} options.streamingService - Service with streamToAsyncIterable and parseChunk
   * @param {Object} options.toolDetectionService - Service with detectToolExecution
   * @param {Object} options.responseHandler - Handler with extractFinalResponse
   * @param {number} [options.maxContentSize=10485760] - Maximum accumulated content size in bytes (default 10MB)
   */
  constructor({ streamingService, toolDetectionService, responseHandler, maxContentSize } = {}) {
    if (!streamingService) {
      throw new Error('streamingService is required');
    }
    if (!toolDetectionService) {
      throw new Error('toolDetectionService is required');
    }
    if (!responseHandler) {
      throw new Error('responseHandler is required');
    }

    this._streamingService = streamingService;
    this._toolDetectionService = toolDetectionService;
    this._responseHandler = responseHandler;
    // Fix #10: Configurable max content size with sensible default
    this._maxContentSize = maxContentSize ?? DEFAULT_MAX_CONTENT_SIZE;
  }

  /**
   * Process a streaming response with SSE event detection and legacy fallback.
   *
   * @param {AsyncIterable|ReadableStream} stream - The response stream to process
   * @param {string} sessionIdForSearchTagRemoval - Session ID used by ResponseHandler to remove search tags from content
   * @param {Object} [callbacks] - Optional callbacks for stream events
   * @param {Function} [callbacks.onChunk] - Called for each raw chunk (decodedChunk, chunkNumber)
   * @param {Function} [callbacks.onThinking] - SSE: Called when thinking event received
   * @param {Function} [callbacks.onText] - SSE: Called with (textChunk, accumulatedText)
   * @param {Function} [callbacks.onToolStart] - SSE: Called with (toolName, toolId)
   * @param {Function} [callbacks.onToolEnd] - SSE: Called with (toolName, toolId)
   * @param {Function} [callbacks.onError] - SSE: Called with (errorMessage)
   * @param {Function} [callbacks.onComplete] - Called when stream completes (responseData)
   * @param {Function} [callbacks.onToolDetected] - Legacy: Called when tool detected ({ toolName, params })
   * @param {Function} [callbacks.onToolResults] - Legacy: Called when tool results detected (results)
   * @param {string} [callbacks.pollSearchId] - TD-2952: searchId from outbound CHECK_SEARCH_RESULTS for response routing
   * @returns {Promise<Object>} Processing result
   */
  async processStream(stream, sessionIdForSearchTagRemoval, callbacks, abortSignal = null) {
    // Fix #1: Use nullish coalescing for consistent null/undefined handling
    const safeCallbacks = callbacks ?? {};
    const pollOptions = safeCallbacks.pollSearchId
      ? { pollSearchId: safeCallbacks.pollSearchId }
      : {};
    const parser = new SSEFrameParser();
    let accumulatedRaw = '';
    let chunkCount = 0;

    // SSE bookkeeping, mutated in place by _consumeSseEvents (kept in one object so the
    // per-event loop can live in a helper and not inflate processStream's complexity).
    const sse = { count: 0, doneReceived: false, errorReceived: false, text: '' };

    // Convert stream to async iterable if needed
    const asyncIterable = this._streamingService.streamToAsyncIterable(stream);

    // Single loop: read stream, feed SSE parser, accumulate raw content
    for await (const chunkEvent of asyncIterable) {
      // TD-2347: stop consuming as soon as the user aborts the turn
      if (abortSignal?.aborted) {
        break;
      }
      const decodedChunk = this._streamingService.parseChunk(chunkEvent);

      // Fix #24: Skip empty chunks to prevent wasted processing
      if (!decodedChunk) {
        continue;
      }

      chunkCount++;

      // Fix #10: Check content size limit before accumulating
      if (accumulatedRaw.length + decodedChunk.length > this._maxContentSize) {
        throw new Error(`Response too large: exceeded ${this._maxContentSize} bytes limit`);
      }

      // Always accumulate raw content (needed for legacy fallback)
      accumulatedRaw += decodedChunk;

      // Always fire onChunk for raw chunk monitoring
      this._safeCallback(safeCallbacks.onChunk, decodedChunk, chunkCount);

      // Parse and dispatch SSE frames from this chunk
      this._consumeSseEvents(parser.feed(decodedChunk), sse, safeCallbacks);
    }

    // TD-2347: if the user stopped mid-stream, skip finalization entirely —
    // stopMessage() has already marked the message "Stopped by user" and any
    // onComplete here would overwrite that with a normal response.
    if (abortSignal?.aborted) {
      return { aborted: true, chunkCount };
    }

    // Flush remaining SSE buffer
    this._consumeSseEvents(parser.flush(), sse, safeCallbacks);

    // Decide path based on whether SSE events were detected
    if (sse.count === 0) {
      // Legacy fallback — process accumulatedRaw the old way
      return this._processLegacyResponse(
        accumulatedRaw,
        chunkCount,
        sessionIdForSearchTagRemoval,
        safeCallbacks,
        pollOptions
      );
    }

    // SSE path — finalize on a clean `done`, OR on a silent stream-end that still
    // produced content (TD-2326: tool-call-first / no-`done` renders). Completion
    // detection (extractFinalResponse → handleSearchTags) must run for these so the
    // search stops polling and the hidden poll message becomes visible.
    // Skip only error-terminated streams (TD-1639: onError already transitioned the
    // message; finalizing would overwrite that state). An `error` event also resets
    // accumulatedText, so a non-recovered error stream has no content to finalize anyway.
    const finalize = sse.doneReceived || (!sse.errorReceived && sse.text.length > 0);
    let responseData;
    if (finalize) {
      responseData = this._responseHandler.extractFinalResponse(
        sse.text,
        sessionIdForSearchTagRemoval,
        pollOptions
      );
      this._safeCallback(safeCallbacks.onComplete, responseData);
    }

    return {
      accumulatedContent: sse.text,
      chunkCount,
      sseEventCount: sse.count,
      doneReceived: sse.doneReceived,
      responseData,
      success: finalize
    };
  }

  /**
   * Consume parsed SSE events: bump the event count, latch the done/error flags, and
   * dispatch each event to its callback (accumulating text). Mutates `sse` in place.
   * Extracted from processStream so both the feed and flush passes share one
   * implementation and processStream stays within cognitive-complexity limits.
   *
   * @param {Iterable<Object>} events - Parsed SSE events from the frame parser
   * @param {{count:number,doneReceived:boolean,errorReceived:boolean,text:string}} sse - Mutable bookkeeping state
   * @param {Object} callbacks - Callback functions for event dispatch
   * @private
   */
  _consumeSseEvents(events, sse, callbacks) {
    for (const event of events) {
      sse.count++;
      if (event.type === 'done') sse.doneReceived = true;
      if (event.type === 'error') sse.errorReceived = true;
      sse.text = this._dispatchSSEEvent(event, callbacks, sse.text);
    }
  }

  /**
   * Dispatch a single parsed SSE event to the appropriate callback.
   *
   * @param {Object} event - Parsed SSE event object
   * @param {Object} callbacks - Callback functions
   * @param {string} accumulatedText - Current accumulated text content
   * @returns {string} Updated accumulated text
   * @private
   */
  _dispatchSSEEvent(event, callbacks, accumulatedText) {
    switch (event.type) {
      case 'thinking':
        this._safeCallback(callbacks.onThinking);
        break;

      case 'text':
        if (event.content) {
          accumulatedText += event.content;
          this._safeCallback(callbacks.onText, event.content, accumulatedText);
        }
        break;

      case 'tool_start':
        this._safeCallback(callbacks.onToolStart, event.tool, event.toolId);
        break;

      case 'tool_end':
        this._safeCallback(callbacks.onToolEnd, event.tool, event.toolId);
        break;

      case 'done':
        // Done is handled after the loop via onComplete
        break;

      case 'error':
        this._safeCallback(callbacks.onError, event.message);
        // Reset accumulated text — if the stream continues (Strands internal retry),
        // new text events should start fresh, not concatenate with pre-error content.
        accumulatedText = '';
        break;

      default:
        // Unknown event type — skip
        break;
    }

    return accumulatedText;
  }

  /**
   * Legacy response processing — used when no SSE events are detected.
   * Runs tool detection regex and response extraction on accumulated raw content.
   *
   * @param {string} accumulatedRaw - Raw accumulated content
   * @param {number} chunkCount - Number of chunks processed
   * @param {string} sessionId - Session ID for search tag removal
   * @param {Object} callbacks - Callback functions
   * @returns {Object} Processing result
   * @private
   */
  _processLegacyResponse(accumulatedRaw, chunkCount, sessionId, callbacks, pollOptions = {}) {
    let toolDetected = false;
    let toolResultsDetected = false;

    // Run tool detection on full accumulated content
    const toolInfo = this._toolDetectionService.detectToolExecution(accumulatedRaw);
    if (toolInfo.detected) {
      toolDetected = true;
      this._safeCallback(callbacks.onToolDetected, {
        toolName: toolInfo.toolName,
        params: toolInfo.params
      });

      if (toolInfo.toolResults) {
        toolResultsDetected = true;
        this._safeCallback(callbacks.onToolResults, toolInfo.toolResults);
      }
    }

    // Extract final response (forward pollSearchId for concurrent-search routing)
    const responseData = this._responseHandler.extractFinalResponse(
      accumulatedRaw,
      sessionId,
      pollOptions
    );
    this._safeCallback(callbacks.onComplete, responseData);

    return {
      accumulatedContent: accumulatedRaw,
      chunkCount,
      toolDetected,
      toolResultsDetected,
      responseData,
      success: true
    };
  }

  /**
   * Safely invoke a callback, catching and logging errors.
   * Fix #6: Returns success/failure for monitoring purposes.
   * @private
   * @param {Function} callback - The callback to invoke
   * @param {...*} args - Arguments to pass to the callback
   * @returns {boolean} True if callback executed successfully, false otherwise
   */
  _safeCallback(callback, ...args) {
    if (typeof callback !== 'function') return true; // No callback = success (nothing to do)

    try {
      callback(...args);
      return true;
    } catch (error) {
      // Log at ERROR level with context for production monitoring
      console.error('StreamingProcessor: Callback error:', error);
      return false;
    }
  }
}

export default StreamingProcessor;
